<template>
	<v-card class="d-flex flex-column pb-5">
		<v-toolbar color="primary" class="pr-3" flat>
			<v-toolbar-title style="flex: 0 1 auto">{{ title }}</v-toolbar-title>

			<!-- Search + org/role/permission filters live in the toolbar (white
			     solo pills), matching the paper / project / QC dashboard toolbars.
			     All four AND together server-side. -->
			<v-text-field
				v-model="searchKey"
				prepend-inner-icon="mdi-magnify"
				placeholder="Search users..."
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				clearable
				bg-color="white"
				class="flex-grow-0 ml-4 user-toolbar-search"
			/>

			<v-autocomplete
				v-if="organizationOptions.length > 1"
				v-model="selectedOrganizationUuid"
				:items="organizationOptions"
				item-title="name"
				item-value="uuid"
				placeholder="Filter by organization"
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				hide-no-data
				auto-select-first
				clearable
				bg-color="white"
				class="flex-grow-0 ml-2 user-toolbar-filter"
			/>

			<v-autocomplete
				v-model="selectedRoleId"
				:items="roleOptions"
				item-title="name"
				item-value="id"
				placeholder="Filter by role"
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				hide-no-data
				auto-select-first
				clearable
				bg-color="white"
				class="flex-grow-0 ml-2 user-toolbar-filter"
			/>

			<v-autocomplete
				v-model="selectedPermissionId"
				:items="permissionOptions"
				item-title="name"
				item-value="id"
				placeholder="Filter by permission"
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				hide-no-data
				auto-select-first
				clearable
				bg-color="white"
				class="flex-grow-0 ml-2 user-toolbar-filter"
			/>

			<v-spacer />

			<v-tooltip location="bottom" open-delay="500" text="Access levels">
				<template #activator="{ props }">
					<v-btn
						v-bind="props"
						class="mr-3 bg-white text-primary px-2"
						elevation="2"
						min-width="0"
						aria-label="Access levels"
						@click="accessLevelsDialog = true"
					>
						<v-icon>mdi-information-outline</v-icon>
					</v-btn>
				</template>
			</v-tooltip>

			<v-dialog
				v-model="accessLevelsDialog"
				max-width="900"
				height="min(85vh, 800px)"
				scrollable
			>
				<access-levels ref="accessLevels" @close="accessLevelsDialog = false" />
			</v-dialog>

			<v-btn
				class="mr-3 bg-white text-primary"
				elevation="2"
				@click="displayDeleted = !displayDeleted"
			>
				<template v-if="!displayDeleted">
					<v-icon start> mdi-eye </v-icon>
					Show deleted
				</template>

				<template v-else>
					<v-icon start> mdi-eye-off </v-icon>
					Hide deleted
				</template>
			</v-btn>

			<v-dialog
				v-model="dialog"
				max-width="800px"
				transition="fade-transition"
				@click:outside="clearSelectedUser"
			>
				<template #activator="{ props }">
					<v-btn v-bind="props" class="bg-secondary" elevation="2">
						<v-icon start> mdi-plus </v-icon>
						Add new user
					</v-btn>
				</template>

				<user-edit
					:edit-mode="dialogEditMode"
					:default-user="selectedUser"
					@cancel="closeDialog"
					@refresh="closeDialogAndRefresh"
				/>
			</v-dialog>

			<v-dialog
				v-model="deleteDialog"
				max-width="800px"
				transition="fade-transition"
			>
				<v-card>
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Delete user</v-toolbar-title>
						<v-hover-icon class="mr-3" />
					</v-toolbar>

					<v-card-title class="text-center py-4">
						Are you sure you want to delete this user?
					</v-card-title>

					<v-divider />

					<v-card-subtitle class="text-center py-4">
						{{ getUserLabel(selectedUser) }}
					</v-card-subtitle>

					<v-divider />

					<v-card-actions class="py-5">
						<v-spacer />

						<v-btn class="bg-secondary" elevation="2" @click="deleteUser">
							Yes
						</v-btn>

						<v-btn class="bg-grey" elevation="2" @click="closeDeleteDialog">
							No
						</v-btn>

						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>

			<v-dialog
				v-model="restoreDialog"
				max-width="800px"
				transition="fade-transition"
			>
				<v-card>
					<v-toolbar color="primary" dark flat>
						<v-toolbar-title>Restore user</v-toolbar-title>
						<v-hover-icon class="mr-3" />
					</v-toolbar>

					<v-card-title class="text-center py-4">
						Are you sure you want to restore this user?
					</v-card-title>

					<v-divider />

					<v-card-subtitle class="text-center py-4">
						{{ getUserLabel(selectedUser) }}
					</v-card-subtitle>

					<v-divider />

					<v-card-actions class="py-5">
						<v-spacer />

						<v-btn class="bg-secondary" elevation="2" @click="restoreUser">
							Yes
						</v-btn>

						<v-btn class="bg-grey" elevation="2" @click="closeRestoreDialog">
							No
						</v-btn>

						<v-spacer />
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-toolbar>

		<v-data-table-server
			v-model:options="options"
			class="user-table"
			item-key="uuid"
			:headers="headers"
			:items="users"
			:row-props="getRowProps"
			:loading="loading"
			:items-length="totalUsers"
		>
			<template #item.email="{ item }">
				{{ item.email }}

				<v-chip
					v-if="isAdmin(item)"
					class="ml-1 px-1 bg-accent text-white"
					label
					size="x-small"
				>
					ADMIN
				</v-chip>

				<!-- v-chip defaults to variant="tonal" with no `variant` attribute, so
				the `secondary` key painted this label at 2.00:1 on a 12% wash of
				itself. Opaque tint + ink instead (DCTL-2283). -->
				<v-chip
					v-if="isSSO(item)"
					class="ml-1 px-1 bg-provenance-b-tint text-provenance-b-ink"
					label
					size="x-small"
					variant="flat"
				>
					SSO
				</v-chip>
			</template>

			<template #item.last_login_at="{ item }">
				{{ getLastLoginTimestamp(item) }}
			</template>

			<template #item.created_at="{ item }">
				{{ getCreatedTimestamp(item) }}
			</template>

			<template #item.organizations="{ item }">
				<div class="d-flex flex-wrap my-2">
					<v-chip
						v-for="organization of item.organizations"
						:key="organization.uuid"
						size="small"
						variant="flat"
						:disabled="!!item.deleted_at"
						class="bg-surface-tint text-fg-1"
					>
						{{ organization.name }}
					</v-chip>

					<v-tooltip
						v-if="!item.organizations.length"
						location="bottom"
						open-delay="500"
					>
						<template #activator="{ props }">
							<v-icon v-bind="props" color="error" class="cursor-default">
								mdi-alert-circle
							</v-icon>
						</template>

						<span>No associated organizations</span>
					</v-tooltip>
				</div>
			</template>

			<template #item.roles="{ item }">
				<div class="d-flex flex-wrap my-2">
					<v-chip
						v-for="role of item.roles"
						:key="role.uuid"
						size="small"
						variant="flat"
						:disabled="!!item.deleted_at"
						class="bg-surface-tint text-fg-1"
					>
						{{ role.name }}
					</v-chip>

					<v-tooltip
						v-if="!item.roles.length"
						location="bottom"
						open-delay="500"
					>
						<template #activator="{ props }">
							<v-icon v-bind="props" color="error" class="cursor-default">
								mdi-alert-circle
							</v-icon>
						</template>

						<span>No associated roles</span>
					</v-tooltip>
				</div>
			</template>

			<template #item.actions="{ item }">
				<v-tooltip v-if="!item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="mr-2"
								:color="hover ? 'secondary' : ''"
								@click="edit(item)"
							>
								mdi-pencil
							</v-icon>
						</v-hover>
					</template>

					<span>Edit</span>
				</v-tooltip>

				<!-- DCTL-2184: admin-only "View as". Hidden for admin rows (an admin
				cannot impersonate another admin) and deleted rows. -->
				<v-tooltip
					v-if="viewerIsAdmin && !item.deleted_at && !isAdmin(item)"
					location="bottom"
					open-delay="500"
				>
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="mr-2"
								:color="hover ? 'primary' : ''"
								role="button"
								aria-label="View as"
								@click="viewAs(item)"
							>
								mdi-account-eye
							</v-icon>
						</v-hover>
					</template>

					<span>View as</span>
				</v-tooltip>

				<v-tooltip v-if="!item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'error' : ''"
								@click="remove(item)"
							>
								mdi-delete
							</v-icon>
						</v-hover>
					</template>

					<span>Delete</span>
				</v-tooltip>

				<v-tooltip v-if="item.deleted_at" location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								:color="hover ? 'secondary' : ''"
								@click="restore(item)"
							>
								mdi-restore
							</v-icon>
						</v-hover>
					</template>

					<span>Restore</span>
				</v-tooltip>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<script>
import _ from "lodash";

import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import { startImpersonation } from "@/utils/impersonation";

import AccessLevels from "@/pages/UserManager/AccessLevels";
import UserEdit from "@/pages/UserManager/UserEdit";
import VHoverIcon from "@/components/VHoverIcon";

export default {
	name: "UserTable",

	components: {
		AccessLevels,
		UserEdit,
		VHoverIcon,
	},
	emits: ["refresh-all"],

	data() {
		return {
			accessLevelsDialog: false,
			deleteDialog: false,
			dialog: false,
			dialogEditMode: false,
			displayDeleted: false,
			loading: false,
			// DCTL-2184: uuid of the row whose "View as" is in flight, to guard
			// against a double-click POSTing /impersonate twice before navigation.
			viewingAsUuid: null,
			options: {},
			organizationOptions: [],
			permissionOptions: [],
			restoreDialog: false,
			roleOptions: [],
			searchKey: "",
			selectedOrganizationUuid: null,
			selectedPermissionId: null,
			selectedRoleId: null,
			selectedUser: {},
			title: "Users",
			totalUsers: 0,
			users: [],
			headers: [
				{
					title: "User email",
					value: "email",
					width: "190px",
				},
				{
					title: "First name",
					value: "first_name",
					width: "125px",
				},
				{
					title: "Last name",
					value: "last_name",
					width: "125px",
				},
				{
					title: "UUID",
					value: "uuid",
					align: "left",
					width: "20%",
				},
				{
					title: "Roles",
					value: "roles",
					align: "left",
					width: "15%",
				},
				{
					title: "Organizations",
					value: "organizations",
					align: "left",
					width: "155px",
				},
				{
					title: "Last login at",
					value: "last_login_at",
					align: "center",
					divider: true,
					width: "165px",
				},
				// {
				// 	title: "Created at",
				// 	value: "created_at",
				// 	align: "center",
				// 	divider: true,
				// 	width: "170px",
				// },
				{
					title: "Actions",
					value: "actions",
					align: "center",
					width: "95px",
					sortable: false,
				},
			],
		};
	},

	computed: {
		// DCTL-2184: whether the CURRENT (viewing) user is an admin. Distinct from
		// the isAdmin(row) method, which reports whether a table ROW is an admin.
		// The user table renders for anyone with a user-management permission, so
		// the "View as" action must be explicitly admin-gated here.
		viewerIsAdmin() {
			return useMainStore().isAdmin;
		},
	},

	watch: {
		// The dialog content stays mounted after its first open (which fetches
		// on mount), so refetch on every later open: roles and user counts may
		// have changed since.
		accessLevelsDialog(open) {
			if (open) {
				this.$refs.accessLevels?.refresh();
			}
		},
		options: {
			deep: true,
			handler() {
				this.getUsers();
			},
		},
		displayDeleted() {
			this.options.page = 1;
			this.getUsers();
		},
		searchKey() {
			this.debouncedRefetch();
		},
		selectedOrganizationUuid() {
			this.options.page = 1;
			this.getUsers();
		},
		selectedRoleId() {
			this.options.page = 1;
			this.getUsers();
		},
		selectedPermissionId() {
			this.options.page = 1;
			this.getUsers();
		},
	},

	mounted() {
		this.fetchOrganizations();
		this.fetchRoles();
		this.fetchPermissions();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		// Debounced so typing doesn't fire a request per keystroke; resetting
		// the page triggers the options deep-watch, but page may already be 1,
		// so call getUsers() explicitly (mirrors the displayDeleted watcher).
		debouncedRefetch: _.debounce(function () {
			this.options.page = 1;
			this.getUsers();
		}, 300),
		fetchOrganizations() {
			this.$http
				.get("/organizations", {
					params: {
						omitDeleted: true,
						omitUsers: true,
						omitProjects: true,
					},
				})
				.then((response) => {
					this.organizationOptions = response.data.organizations;
				})
				.catch((error) => {
					if (error.response?.data) {
						this.showNotification(
							error.response.data.error || error.response.data,
						);
					}
				});
		},
		// /roles and /permissions getAll are gated by the same
		// `can:create_user or edit_user` family as the Users table, so any user
		// who can see this table can populate these filters. Unpaginated (no
		// page param) returns the full list in `.data`.
		fetchRoles() {
			this.$http
				.get("/roles", { params: { omitDeleted: true } })
				.then((response) => {
					this.roleOptions = response.data.roles.data;
				})
				.catch((error) => {
					if (error.response?.data) {
						this.showNotification(
							error.response.data.error || error.response.data,
						);
					}
				});
		},
		fetchPermissions() {
			this.$http
				.get("/permissions", { params: { omitDeleted: true } })
				.then((response) => {
					this.permissionOptions = response.data.permissions.data;
				})
				.catch((error) => {
					if (error.response?.data) {
						this.showNotification(
							error.response.data.error || error.response.data,
						);
					}
				});
		},
		isAdmin(user) {
			// Check every role, not just roles[0]: an admin whose administrator
			// role is not listed first must still be detected (else the client
			// would offer "View as" against an admin the server would refuse).
			return (user.roles ?? []).some((role) => role.slug === "administrator");
		},
		// DCTL-2184: start viewing the app as this user. The server swaps the
		// session identity and refuses admins; startImpersonation re-reads
		// /users/me + permissions so the whole SPA re-scopes to the target.
		async viewAs(item) {
			// Guard against a double-click firing two /impersonate POSTs.
			if (this.viewingAsUuid) {
				return;
			}
			this.viewingAsUuid = item.uuid;
			try {
				await startImpersonation(item.uuid);
				this.$router.push("/home");
			} catch (error) {
				console.error(error);
				this.showNotification({
					message: `Could not view as ${item.first_name} ${item.last_name}.`,
					status: "error",
				});
			} finally {
				this.viewingAsUuid = null;
			}
		},
		isSSO(user) {
			return user.sso_enabled;
		},
		getRowProps(user) {
			return {
				class: this.isDeleted(user),
			};
		},
		isDeleted(user) {
			return user.deleted_at ? "deleted" : "";
		},
		getUserLabel(user) {
			return `${user.first_name} ${user.last_name} (${user.email})`;
		},
		getLastLoginTimestamp(user) {
			return user.last_login_at
				? this._getLocaleTimestamp(user.last_login_at)
				: "-";
		},
		getCreatedTimestamp(user) {
			return this._getLocaleTimestamp(user.created_at);
		},
		_getLocaleTimestamp(timestamp) {
			return new Date(timestamp).toLocaleString();
		},
		clearSelectedUser() {
			this.dialogEditMode = false;
			setTimeout(() => {
				this.selectedUser = {};
			}, 300);
		},
		remove(item) {
			this.selectedUser = Object.assign({}, item);

			this.deleteDialog = true;
		},
		closeDeleteDialog() {
			this.deleteDialog = false;

			this.clearSelectedUser();
		},
		deleteUser() {
			this.loading = "secondary";

			if (this.selectedUser) {
				this.$http
					.delete(`/users/${this.selectedUser.uuid}`)
					.then((response) => {
						// display success
						this.showNotification(response.data);

						// refresh table
						this.$emit("refresh-all");
					})
					.catch((error) => {
						// display error
						if (error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}

						this.loading = false;
					});
			} else {
				this.loading = false;
			}

			this.closeDeleteDialog();
		},
		restore(item) {
			this.selectedUser = Object.assign({}, item);

			this.restoreDialog = true;
		},
		closeRestoreDialog() {
			this.restoreDialog = false;

			this.clearSelectedUser();
		},
		restoreUser() {
			this.loading = "secondary";

			if (this.selectedUser) {
				this.$http
					.put(`/users/${this.selectedUser.uuid}`, {
						deleted_at: null,
					})
					.then((response) => {
						// display success
						this.showNotification(response.data);

						// refresh table
						this.$emit("refresh-all");
					})
					.catch((error) => {
						// display error
						if (error.response) {
							if (error.response.data.message) {
								this.showNotification(error.response.data);
							} else {
								this.showNotification(error.response.data.error);
							}
						}

						this.loading = false;
					});
			} else {
				this.loading = false;
			}

			this.closeRestoreDialog();
		},
		edit(item) {
			this.selectedUser = Object.assign({}, item);
			this.dialogEditMode = true;
			this.dialog = true;
		},
		closeDialog() {
			this.dialog = false;
			this.dialogEditMode = false;

			setTimeout(() => {
				this.clearSelectedUser();
			}, 250);
		},
		closeDialogAndRefresh() {
			this.closeDialog();
			this.$emit("refresh-all");
		},
		getUsers() {
			this.loading = "secondary";

			const { itemsPerPage, page, sortBy, sortDesc } = this.options;

			this.$http
				.get("/users", {
					params: {
						...(this.displayDeleted ? { deletedOnly: true } : {}),
						...(!this.displayDeleted ? { omitDeleted: true } : {}),
						filter: this.searchKey || undefined,
						organization: this.selectedOrganizationUuid || undefined,
						role: this.selectedRoleId || undefined,
						permission: this.selectedPermissionId || undefined,
						itemsPerPage: itemsPerPage,
						page: page,
						sortBy: sortBy,
						sortDesc: sortDesc,
					},
				})
				.then((response) => {
					this.users = response.data.users.data;
					this.totalUsers = parseInt(response.data.users.meta.total, 10);
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		refresh() {
			this.getUsers();
		},
	},
};
</script>

<style scoped lang="scss">
:deep(.user-table) {
	&.v-data-table .v-table__wrapper td {
		font-size: 12px;
	}

	&.v-data-table .v-table__wrapper th {
		font-size: 12px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.6);
		height: 48px;
	}
}

.user-toolbar-search {
	min-width: 190px;
	max-width: 260px;
}

.user-toolbar-filter {
	min-width: 160px;
	max-width: 210px;
}
</style>
